/**
 * List-level sibling order filters and sorting utilities.
 * @module siblingOrderFilter/orderFilters
 */
import { SiblingOrder } from "../../../data-model/siblingOrdersModel";
import { MedicalOrderStatus } from "../../../enums/orderReviewStatus";
import { toTime, toYearMonthKey } from "./dateUtils";

/**
 * Filters sibling orders down to those that have not been worked on yet, i.e.
 * whose medical order status is still "yet to start work on order".
 *
 * @param orders - Sibling orders to filter
 * @returns Only the sibling orders that are still awaiting work
 */
export const filterOnlyNotWorkedOrders = (orders: SiblingOrder[]) => {
  return orders.filter(
    (order) =>
      order?.order?.status?.medical_order_status ===
        MedicalOrderStatus.YET_TO_START_WORK_ON_ORDER ||
      MedicalOrderStatus.ORDER_IN_PROGRESS,
  );
};

/**
 * Filters sibling orders down to those whose date of service falls in the same
 * month and year as the parent order's date of service.
 *
 * @param parentOrderDos - The parent order's date of service, used as the
 *   reference point for the month/year comparison
 * @param orders - Sibling orders to filter
 * @returns Sibling orders that share the parent order's service month and year;
 *   an empty array when the parent's date of service is missing or invalid
 */
export const filterOrdersWhichAreOfCurrentMonthAndYear = (
  parentOrderDos: string,
  orders: SiblingOrder[],
) => {
  const parentKey = toYearMonthKey(parentOrderDos);
  if (!parentKey) {
    return [];
  }
  return orders.filter(
    (order) =>
      toYearMonthKey(order?.order?.payload?.date_of_service) === parentKey,
  );
};

/**
 * Filters sibling orders down to those that are upcoming relative to the parent
 * order, i.e. whose date of service is strictly after the parent order's date
 * of service.
 *
 * @param parentOrderDos - The parent order's date of service, used as the
 *   reference point for the comparison
 * @param orders - Sibling orders to filter
 * @returns Sibling orders whose date of service is later than the parent's; an
 *   empty array when the parent's date of service is missing or invalid
 */
export const filterUpcomingOrders = (
  parentOrderDos: string,
  orders: SiblingOrder[],
) => {
  const parentTime = toTime(parentOrderDos);
  if (parentTime === null) {
    return [];
  }
  return orders.filter((order) => {
    const orderTime = toTime(order?.order?.payload?.date_of_service);
    return orderTime !== null && orderTime > parentTime;
  });
};

/**
 * Sorts sibling orders in ascending order of their date of service.
 *
 * Orders with a missing or invalid date of service are pushed to the end of
 * the list. The input array is not mutated; a new sorted array is returned.
 *
 * @param orders - Sibling orders to sort
 * @returns A new array of sibling orders sorted by date of service, earliest
 *   first
 */
export const sortSiblingOrdersByDosAscending = (
  orders: SiblingOrder[],
): SiblingOrder[] => {
  return [...orders].sort((a, b) => {
    const timeA = toTime(a?.order?.payload?.date_of_service);
    const timeB = toTime(b?.order?.payload?.date_of_service);
    if (timeA === null) {
      return timeB === null ? 0 : 1;
    }
    if (timeB === null) {
      return -1;
    }
    return timeA - timeB;
  });
};

/**
 * Takes the first `count` sibling orders from the list.
 *
 * When `count` is greater than or equal to the number of orders, the full list
 * is returned; otherwise only the first `count` orders are returned. The input
 * array is not mutated; a new array is returned.
 *
 * @param orders - Sibling orders to take from
 * @param count - Maximum number of orders to return (values below zero are
 *   treated as zero)
 * @returns The first `count` sibling orders, or all orders when `count` exceeds
 *   the list length
 */
export const takeFirstSiblingOrders = (
  orders: SiblingOrder[],
  count: number,
): SiblingOrder[] => {
  return orders.slice(0, Math.max(0, count));
};
